// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Box, Grid, ResponsiveContext } from 'grommet'
import { useNavigate } from 'react-router-dom'
import {
  Video,
  UserAdd,
  AppsRounded,
  Cycle,
  ServerCluster,
  Close
} from 'grommet-icons'
import { useFlags } from 'launchdarkly-react-client-sdk'

import {
  ModalDialog,
  ModalHeader,
  Typography,
  Button,
  Card
} from '../../../components'
import { isV2 } from '../../../utils/feature-flag-utils'

const columns = {
  xsmall: ['auto'],
  small: ['auto'],
  medium: ['auto'],
  large: ['auto'],
  xlarge: ['auto', 'auto', 'auto']
}
const NextStepsModal = ({ setIsGuidedNextStepsModalOpen }) => {
  const { t } = useTranslation(['dashboard'])
  const size = useContext(ResponsiveContext)
  const navigate = useNavigate()
  const LDFlags = useFlags()
  const videoTutorialLD = LDFlags['glcp-videos-tutorial']

  const closeModal = () => {
    sessionStorage.removeItem('glcpGuidedWorkspace')
    setIsGuidedNextStepsModalOpen(false)
  }

  const cards = [
    {
      icon: <AppsRounded color="brand" size="xxlarge" />,
      id: 1,
      title: t('dashboard:guided_workspace.explore_services_title'),
      text: t('dashboard:guided_workspace.explore_services_text'),
      route: '/services/service-catalog'
    },
    {
      icon: <UserAdd color="brand" size="xxlarge" />,
      id: 2,
      title: isV2()
        ? t('dashboard:guided_workspace.add_users_title')
        : t('dashboard:guided_workspace.invite_users_title'),
      text: isV2()
        ? t('dashboard:guided_workspace.add_users_text')
        : t('dashboard:guided_workspace.invite_users_text'),
      route: '/manage-account/identity/users'
    },

    {
      icon: <Cycle color="brand" size="xxlarge" />,
      id: 3,
      title: t('dashboard:guided_workspace.add_service_subscription_title'),
      text: t('dashboard:guided_workspace.add_service_subscription_text'),
      route: '/services/service-subscriptions'
    },
    {
      icon: <ServerCluster color="brand" size="xxlarge" />,
      id: 4,
      title: t('dashboard:guided_workspace.add_devices_title'),
      text: t('dashboard:guided_workspace.add_devices_text'),
      route: '/devices/inventory-list'
    },
    {
      icon: <Cycle color="brand" size="xxlarge" />,
      id: 5,
      title: t('dashboard:guided_workspace.add_device_subscription_title'),
      text: t('dashboard:guided_workspace.add_device_subscription_text'),
      route: '/devices/subscriptions-list'
    },
    {
      icon: <Video color="brand" size="xxlarge" />,
      id: 6,
      title: t('dashboard:guided_workspace.video_tutorial_title'),
      text: t('dashboard:guided_workspace.video_tutorial_text'),
      route: videoTutorialLD ? '/home/videos-tutorials' : '/home/get-started'
    }
  ]

  return (
    <ModalDialog
      testId="next-steps-modal"
      // width="large"
      onClose={() => closeModal()}
      header={
        <ModalHeader
          title={
            <Box pad={{ bottom: 'small' }}>
              <Box
                direction="row"
                justify="between"
                data-testid="next-steps-modal-title"
              >
                <Typography type="heading" level="2">
                  {t('dashboard:guided_workspace.next_steps_modal_title')}
                </Typography>
                <Button
                  icon={<Close size="small" />}
                  onClick={() => closeModal()}
                />
              </Box>
              <Box data-testid="next-steps-modal-subtitle">
                <Typography type="text">
                  {t('dashboard:guided_workspace.next_steps_modal_subtitle')}
                </Typography>
              </Box>
            </Box>
          }
        />
      }
      content={
        <Grid
          width="large"
          columns={columns[size]}
          gap="medium"
          pad={{
            left: 'small',
            right: 'small',
            top: 'small',
            bottom: 'medium'
          }}
        >
          {cards?.map((item) => (
            <Card
              icon={item?.icon}
              title={
                <Box data-testid={`next-steps-card-title-${item?.id}`}>
                  <Typography type="heading" level="2">
                    {item?.title}
                  </Typography>
                </Box>
              }
              description={
                <Box pad={{ top: 'xsmall' }}>
                  <Typography size="small" type="text">
                    {item?.text}
                  </Typography>
                </Box>
              }
              elevation="small"
              cardWidth="100%"
              onClick={() => {
                if (
                  item?.route === '/home/get-started' ||
                  item?.route === '/home/videos-tutorials'
                )
                  sessionStorage.removeItem('glcpGuidedWorkspace')
                navigate(item.route)
              }}
            />
          ))}
        </Grid>
      }
      footer={
        <Box
          width="medium"
          alignSelf="center"
          data-testid="explore-on-your-own-btn"
        >
          <Button
            label={t('dashboard:guided_workspace.explore_btn')}
            onClick={() => closeModal()}
          />
        </Box>
      }
    />
  )
}

NextStepsModal.propTypes = {
  setIsGuidedNextStepsModalOpen: PropTypes.bool.isRequired
}
export default NextStepsModal
